import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DAYS } from "@/data/itinerary";
import { MapPin } from "lucide-react";

// Custom terracotta numbered marker
function makeIcon(num, active = false) {
  const size = active ? 44 : 36;
  const ring = active ? 8 : 6;
  const html = `
    <div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        position:absolute;inset:0;
        border-radius:9999px;
        background:rgba(217,108,74,0.25);
        transform:scale(${active ? 1.4 : 1});
        transition:transform .2s ease;
      "></div>
      <div style="
        position:relative;
        width:${size - ring * 2}px;
        height:${size - ring * 2}px;
        border-radius:9999px;
        background:#D96C4A;
        color:white;
        display:flex;
        align-items:center;
        justify-content:center;
        font-family:Outfit,sans-serif;
        font-weight:600;
        font-size:${active ? 15 : 13}px;
        box-shadow:0 2px 8px rgba(29,58,76,0.3);
        border:3px solid white;
      ">${num}</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: "elba-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

// Elba island center
const ELBA_CENTER = [42.777, 10.237];

export default function MapPage() {
  const [active, setActive] = useState(null);
  // Delay leaflet mount to bypass React 19 StrictMode double-init that
  // triggers "Map container is already initialized" in react-leaflet 4.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="map-page" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <div className="font-accent text-xs text-[#D96C4A] mb-3">
            La mappa del viaggio
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1D3A4C]">
            Otto tappe sull'isola
          </h1>
          <p className="mt-3 text-[#5C7180] max-w-2xl">
            Zooma, sposta la mappa e tocca un pallino per aprire la giornata.
          </p>
        </div>

        <div
          className="relative rounded-3xl overflow-hidden border border-[#1D3A4C]/10 shadow-sm"
          data-testid="map-container"
        >
          {ready ? (
            <MapContainer
              center={ELBA_CENTER}
              zoom={11}
              scrollWheelZoom={false}
              style={{ height: "560px", width: "100%" }}
              className="z-0"
            >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {DAYS.map((d) => (
              <Marker
                key={d.id}
                position={[d.coords.lat, d.coords.lng]}
                icon={makeIcon(d.id, active === d.id)}
                eventHandlers={{
                  mouseover: () => setActive(d.id),
                  mouseout: () => setActive(null),
                  click: () => setActive(d.id),
                }}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div
                      className="text-[10px] uppercase tracking-widest"
                      style={{ color: "#D96C4A" }}
                    >
                      Giorno {d.id} · {d.zone}
                    </div>
                    <div
                      className="font-semibold mt-1"
                      style={{
                        color: "#1D3A4C",
                        fontFamily: "Playfair Display, serif",
                        fontSize: "18px",
                      }}
                    >
                      {d.title}
                    </div>
                    <div
                      className="text-xs mt-1"
                      style={{ color: "#5C7180" }}
                    >
                      {d.coords.label}
                    </div>
                    <Link
                      to={`/giorno/${d.id}`}
                      data-testid={`map-popup-link-${d.id}`}
                      style={{
                        display: "inline-block",
                        marginTop: "10px",
                        color: "#D96C4A",
                        fontWeight: 500,
                        fontSize: "13px",
                      }}
                    >
                      Apri la giornata →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          ) : (
            <div
              style={{ height: "560px" }}
              className="w-full flex items-center justify-center bg-[#F5EFE6]"
            >
              <div className="w-6 h-6 rounded-full border-2 border-[#D96C4A]/30 border-t-[#D96C4A] animate-spin" />
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {DAYS.map((d) => (
            <Link
              key={d.id}
              to={`/giorno/${d.id}`}
              data-testid={`map-legend-${d.id}`}
              onMouseEnter={() => setActive(d.id)}
              onMouseLeave={() => setActive(null)}
              className={`group flex items-start gap-4 p-4 rounded-2xl border transition-all ${
                active === d.id
                  ? "border-[#D96C4A] bg-[#F5EFE6]"
                  : "border-[#1D3A4C]/10 bg-white hover:border-[#D96C4A]/50"
              }`}
            >
              <span className="w-9 h-9 rounded-full bg-[#D96C4A] text-white flex items-center justify-center font-display shrink-0">
                {d.id}
              </span>
              <div className="min-w-0">
                <div className="font-accent text-[10px] text-[#8A9BA8]">
                  {d.zone}
                </div>
                <div className="font-display text-[#1D3A4C] truncate">
                  {d.title}
                </div>
                <div className="mt-1 text-xs text-[#5C7180] inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {d.coords.label}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
